import { useTranslation } from 'react-i18next';
import { X, Star } from 'lucide-react';
import { Button, Badge } from '@brainsway/ui';
import type { TherapistAssignment as TherapistAssignmentType } from '../types';

interface TherapistAssignmentProps {
  therapists: TherapistAssignmentType[];
  onUnassign?: (therapistId: string) => void;
  isAdmin?: boolean;
}

export function TherapistAssignment({ therapists, onUnassign, isAdmin }: TherapistAssignmentProps) {
  const { t } = useTranslation();

  if (therapists.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-neutral-400">
        {t('hospitals.noEmployees', 'No employees assigned')}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {therapists.map((th) => (
        <div
          key={th.id}
          className="flex items-center justify-between rounded-2xl border border-gray-200 px-3 py-2 hover:bg-neutral-50 cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700">
              {(th.firstName?.[0] || '') + (th.lastName?.[0] || '')}
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-sm font-medium text-neutral-800">
                {th.firstName} {th.lastName}
                {th.isPrimary && (
                  <Star className="h-3.5 w-3.5 fill-warning-400 text-warning-400" />
                )}
              </div>
              <p className="text-xs text-neutral-500">{th.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {th.isPrimary && (
              <Badge variant="warning">{t('hospitals.primary', 'Primary')}</Badge>
            )}
            {isAdmin && onUnassign && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onUnassign(th.id)}
                className="text-error-500"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
